import { useState, useEffect, useContext, useRef } from "react";
import React from "react";
import { Table, Tabs, Button, Spin, notification, Card, Space, Select, Form, Input, Modal, Descriptions, Tag } from "antd";
import axiosInstance from "../Components/axiosInstance";
import { UserContext } from "../Components/UserContext";
import API_ENDPOINTS from "../apiConfig";
import { downloadAsCSV } from "../utils/downloadAsCSV";
import "../styles/CampaignManager.module.css"; 

const { TabPane } = Tabs;
const { Option } = Select;
const EditableContext = React.createContext(null);
const STATUS_OPTIONS = ["new", "for_review", "approved", "sending", "sent", "replied", "error", "followup_sent", "mail_merge", "delay_send", "unsubscribe"];
const STATUS_COLORS = {
  new: "green",
  for_review: "blue",
  approved: "geekblue",
  sending: "gold",
  sent: "purple",
  replied: "cyan",
  error: "red",
  followup_sent: "volcano",
  mail_merge: "orange",
  delay_send: "lime",
  unsubscribe: "magenta",
};


const EditableRow = ({ index, ...props }) => {
  const [form] = Form.useForm();
  return (
    <Form form={form} component={false}>
      <EditableContext.Provider value={form}>
        <tr {...props} />
      </EditableContext.Provider>
    </Form>
  );
};

const EditableCell = ({ title, editable, children, dataIndex, record, handleSave, ...restProps }) => {
  const [editing, setEditing] = useState(false);
  const inputRef = useRef(null);
  const form = useContext(EditableContext);

  useEffect(() => {
    if (editing && inputRef.current) {
      inputRef.current.focus?.();
    }
  }, [editing]);

  const toggleEdit = () => {
    setEditing(!editing);
    form.setFieldsValue({ [dataIndex]: record[dataIndex] });
  };

  const save = async () => {
    try {
      const values = await form.validateFields();
      toggleEdit();
      handleSave({ ...record, ...values });
    } catch (errInfo) {
      console.log("Save failed:", errInfo);
    }
  };

  let childNode = children;

  if (editable) {
    const isStatus = dataIndex === "status";

    childNode = editing ? (
      <Form.Item style={{ margin: 0 }} name={dataIndex}>
        {isStatus ? (
          <Form.Item style={{ margin: 0 }} name={dataIndex}>
            <Select
              onChange={(value) => {
                  form.setFieldValue(dataIndex, value);
                  setTimeout(save, 0); // Ensure state update before save
                }}
            >
              {STATUS_OPTIONS.map(s => (
                <Option key={s} value={s}>{s}</Option>
              ))}
            </Select>
          </Form.Item>
        ) : (
          <Input onPressEnter={save} onBlur={save} />
        )}
      </Form.Item>
    ) : (
      <div className="editable-cell-value-wrap" style={{ paddingInlineEnd: 24 }} onClick={toggleEdit}>
        {children}
      </div>
    );
  }

  return <td {...restProps}>{childNode}</td>;
};

export default function CampaignManager() {
  const { token } = useContext(UserContext);
  const [campaigns, setCampaigns] = useState([]);
  const [selectedGroup, setSelectedGroup] = useState("all");
  const [rateLimits, setRateLimits] = useState([]);
  const [loading, setLoading] = useState(false);
  const [campaignOptions, setCampaignOptions] = useState([]);
  const [selectedCampaignId, setSelectedCampaignId] = useState("");
  const [summaryData, setSummaryData] = useState(null);
  const [viewRecord, setViewRecord] = useState(null);

  const [personalizeCount, setPersonalizeCount] = useState(5);
  const [runBatchSize, setRunBatchSize] = useState(5);

  const axios = axiosInstance(token);

  const renderPreview = (text = "") => {
    const preview = text.split("\n").slice(0, 2).join(" ").slice(0, 100);
    return <span title={text}>{preview.length < text.length ? preview + "..." : preview}</span>;
  };

  const openDetailModal = (record) => {
    setViewRecord(record);
  };

  const columnGroups = {
    messaging: [
      {
        title: "Messaging",
        children: [
          {
        title: "Campaign ID", dataIndex: "campaign_id", key: "campaign_id"
      },
      {
        title: "Status",
        dataIndex: "status",
        key: "status",
        filters: STATUS_OPTIONS.map((s) => ({ text: s, value: s })),
        onFilter: (value, record) => record.status === value,
        sorter: (a, b) => a.status.localeCompare(b.status),
        editable: true,
        render: (status) => (
          <Tag color={STATUS_COLORS[status] || "default"} key={status}>
            {status}
          </Tag>
        )
      },
          { title: "Email Subject", dataIndex: "email_subject", key: "email_subject", editable: true, render: renderPreview },
          // { title: "Custom Intro", dataIndex: "custom_intro", key: "custom_intro", editable: true, render: renderPreview },
          // { title: "Email Body Snippet", dataIndex: "email_body_snippet", key: "email_body_snippet", render: renderPreview },
          { title: "Email Body", dataIndex: "email_body", key: "email_body", editable: true, render: renderPreview },
          { title: "Search Summary", dataIndex: "search_summary", key: "search_summary", render: renderPreview },
          { title: "Follow Up Body", dataIndex: "follow_up_body", key: "follow_up_body", editable: true, render: renderPreview },
          { title: "LinkedIn Connect", dataIndex: "linkedin_connect", key: "linkedin_connect", editable: true, render: renderPreview },
          { title: "LinkedIn Follow Up", dataIndex: "linkedin_followup", key: "linkedin_followup", editable: true, render: renderPreview },
        ],
      },
      {
        title: 'Actions',
        dataIndex: 'view',
        render: (_, record) => <Button onClick={() => openDetailModal(record)} size="small">View</Button>,
      },
    ],

    contact: [
      {
        title: "Campaign ID", dataIndex: "campaign_id", key: "campaign_id"
      },
      {
        title: "Status",
        dataIndex: "status",
        key: "status",
        filters: STATUS_OPTIONS.map((s) => ({ text: s, value: s })),
        onFilter: (value, record) => record.status === value,
        sorter: (a, b) => a.status.localeCompare(b.status),
        editable: true,
        render: (status) => (
          <Tag color={STATUS_COLORS[status] || "default"} key={status}>
            {status}
          </Tag>
        )
      },
      {
        title: "Contact Info",
        children: [
          { title: "Org", dataIndex: "organization", key: "organization", sorter: true },
          { title: "Role", dataIndex: "role", key: "role", sorter: true },
          { title: "First Name", dataIndex: "first_name", key: "first_name", sorter: (a, b) => a.first_name.localeCompare(b.first_name) },
          { title: "Last Name", dataIndex: "last_name", key: "last_name", sorter: true },
          { title: "Email", dataIndex: "email", key: "email", sorter: true },
        ],
      },
      {
        title: 'Actions',
        dataIndex: 'view',
        render: (_, record) => <Button onClick={() => openDetailModal(record)} size="small">View</Button>,
      },
    ],

    all: [
      { title: "Campaign ID", dataIndex: "campaign_id", key: "campaign_id" },
      {
        title: "Status",
        dataIndex: "status",
        key: "status",
        filters: STATUS_OPTIONS.map((s) => ({ text: s, value: s })),
        onFilter: (value, record) => record.status === value,
        sorter: (a, b) => a.status.localeCompare(b.status),
        editable: true,
        render: (status) => (
          <Tag color={STATUS_COLORS[status] || "default"} key={status}>
            {status}
          </Tag>
        )
      },
      {
        title: "Contact Info",
        children: [
          { title: "Org", dataIndex: "organization", key: "organization", sorter: true },
          { title: "Role", dataIndex: "role", key: "role", sorter: true },
          { title: "First Name", dataIndex: "first_name", key: "first_name", sorter: true },
          { title: "Last Name", dataIndex: "last_name", key: "last_name", sorter: true },
          { title: "Email", dataIndex: "email", key: "email", sorter: true },
        ],
      },
      {
        title: "Email Details",
        children: [
          { title: "New Email Status", dataIndex: "new_email_status", key: "new_email_status", sorter: true },
          { title: "Email Version", dataIndex: "email_version", key: "email_version", sorter: true },
          { title: "Last Sent", dataIndex: "last_sent_at", key: "last_sent_at" },
          { title: "Send Count", dataIndex: "send_count", key: "send_count" },
          { title: "Sender User", dataIndex: "sender_user", key: "sender_user" },
          { title: "Unsubscribe", dataIndex: "unsubscribe", key: "unsubscribe" },
          { title: "Reply Received At", dataIndex: "reply_received_at", key: "reply_received_at" },
          { title: "Auto Replied", dataIndex: "auto_replied", key: "auto_replied" },
          { title: "Owner Name", dataIndex: "owner_name", key: "owner_name", sorter: true },
          { title: "Owner Email", dataIndex: "owner_email", key: "owner_email", sorter: true },
          { title: "Org Domain", dataIndex: "org_domain", key: "org_domain", sorter: true },
        ],
      },
      {
        title: "Messaging",
        children: [
          { title: "Email Subject", dataIndex: "email_subject", key: "email_subject", editable: true, render: renderPreview },
          // { title: "Custom Intro", dataIndex: "custom_intro", key: "custom_intro", editable: true, render: renderPreview },
          // { title: "Email Body Snippet", dataIndex: "email_body_snippet", key: "email_body_snippet", render: renderPreview },
          { title: "Email Body", dataIndex: "email_body", key: "email_body", editable: true, render: renderPreview },
          { title: "Search Summary", dataIndex: "search_summary", key: "search_summary", render: renderPreview },
          { title: "Follow Up Body", dataIndex: "follow_up_body", key: "follow_up_body", editable: true, render: renderPreview },
          { title: "LinkedIn Connect", dataIndex: "linkedin_connect", key: "linkedin_connect", editable: true, render: renderPreview },
          { title: "LinkedIn Follow Up", dataIndex: "linkedin_followup", key: "linkedin_followup", editable: true, render: renderPreview },
        ],
      },
      {
        title: 'Actions',
        dataIndex: 'view',
        render: (_, record) => <Button onClick={() => openDetailModal(record)} size="small">View</Button>,
      },
    ],
  };


  const fetchCampaigns = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API_ENDPOINTS.email}/campaigns`);
      setCampaigns(res.data);

      // Extract unique campaign IDs
      const ids = [...new Set(res.data.map((c) => c.campaign_id).filter(Boolean))];
      setCampaignOptions(ids);
    } catch (err) {
      notification.error({ message: "Error loading campaigns", description: err.message });
    } finally {
      setLoading(false);
    }
  };

   const handleSave = async (row) => {
    const newData = [...campaigns];
    const index = newData.findIndex((item) => row.id === item.id);
    const item = newData[index];
    newData.splice(index, 1, { ...item, ...row });
    setCampaigns(newData);
    try {
      await axios.post(`${API_ENDPOINTS.email}/update-campaign`, row);
    } catch (error) {
      notification.error({ message: "Failed to update", description: error.message });
    }
  };

    const fetchSummary = async (campaignId) => {
    if (!campaignId) return;
    setLoading(true);
    try {
        const res = await axios.get(`${API_ENDPOINTS.email}/campaign-summary?campaign_id=${campaignId}`);
        setSummaryData(res.data);
        console.log("Summary Data:", res.data); // Debugging
    } catch (err) {
        notification.error({ message: "Error loading summary", description: err.message });
    } finally {
        setLoading(false);
    }
 };


  const fetchRateLimits = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API_ENDPOINTS.email}/rate-limits`);
      setRateLimits(res.data);
      console.log("Rate Limits Data:", res.data); // Debugging
    } catch (err) {
      notification.error({ message: "Error loading rate limits", description: err.message });
    } finally {
      setLoading(false);
    }
  };

  const triggerAction = async (endpoint, payload = {}) => {
    if (!selectedCampaignId && endpoint !== "follow-up") {
      notification.warning({ message: "Please select a campaign ID first." });
      return;
    }

    try {
      setLoading(true);
      const res = await axios.post(`${API_ENDPOINTS.email}/${endpoint}`, payload);
      notification.success({ message: res.data.message || "Action triggered" });
      fetchCampaigns();
    } catch (err) {
      notification.error({ message: "Action failed", description: err.message });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCampaigns();
    fetchRateLimits();
  }, []);

  const rateLimitColumns = [
    { title: "Sender", dataIndex: "user", key: "user",sorter: true  },
    { title: "Daily Count", dataIndex: "daily_count", key: "daily_count", sorter: true },
    { title: "Limit", dataIndex: "daily_limit", key: "daily_limit" },
    { title: "Account Created", dataIndex: "acct_created", key: "acct_created" },
    { title: "Account Age", dataIndex: "acct_age", key: "acct_age" },
    { title: "Status", dataIndex: "status", key: "status" }
  ];

  const components = {
    body: {
      row: EditableRow,
      cell: EditableCell,
    },
  };

 const columns = columnGroups[selectedGroup].map((col) => {
    if (!col.children && !col.editable) return col;
    if (col.children) {
      return {
        ...col,
        children: col.children.map((child) =>
          child.editable
            ? {
                ...child,
                onCell: (record) => ({
                  record,
                  editable: true,
                  dataIndex: child.dataIndex,
                  title: child.title,
                  handleSave,
                }),
              }
            : child
        ),
      };
    }

    if (col.editable) {
      return {
        ...col,
        onCell: (record) => ({
          record,
          editable: true,
          dataIndex: col.dataIndex,
          title: col.title,
          handleSave,
        }),
      };
    }

    return col;
  });


  return (
    <Card title="Email Campaign Manager" style={{ margin: 20 }}>
      <Tabs defaultActiveKey="summary">
        <TabPane tab="Summary" key="summary">
            <div style={{ display: "flex", flexDirection: "column", alignItems: "flex-start", gap: 16 }}>
                <Select
                style={{ width: 250 }}
                placeholder="Select Campaign ID"
                value={selectedCampaignId || undefined}
                onChange={(value) => {
                    setSelectedCampaignId(value);
                    fetchSummary(value);
                }}
                allowClear
                >
                {campaignOptions.map((id) => (
                    <Option key={id} value={id}>
                    {id}
                    </Option>
                ))}
                </Select>
                <Button
                  onClick={() => {
                    if (!summaryData) return;
                    const exportable = Object.entries(summaryData.counts || {}).map(([status, count]) => ({
                      campaign_id: summaryData.campaign_id,
                      status,
                      count,
                      total_records: summaryData.total_records,
                      replies: summaryData.replies
                    }));
                    downloadAsCSV(exportable, `summary_${summaryData.campaign_id}.csv`);
                  }}
                >
                  Export Summary CSV
                </Button>


                {loading ? (
                <Spin />
                ) : summaryData ? (
                <Card title={`Summary for ${summaryData.campaign_id}`} size="small" style={{ width: "100%" }}>
                    <p><strong>Total Records:</strong> {summaryData.total_records}</p>
                    <p><strong>Replies:</strong> {summaryData.replies}</p>
                    <p><strong>Status Counts:</strong></p>
                    <ul>
                    {Object.entries(summaryData.counts).map(([status, count]) => (
                        <li key={status}>
                        <strong>{status}:</strong> {count}
                        </li>
                    ))}
                    </ul>
                </Card>
                ) : (
                <p>Select a campaign ID to see its summary.</p>
                )}
            </div>
        </TabPane>
        <TabPane tab="Campaigns" key="campaigns">
         <Space direction="vertical" style={{ marginBottom: 16, width: "100%" }}>
            <Space wrap align="center">
              <Select
                style={{ width: 200 }}
                placeholder="Select Campaign ID"
                value={selectedCampaignId || undefined}
                onChange={setSelectedCampaignId}
                allowClear
              >
                {campaignOptions.map((id) => (
                  <Option key={id} value={id}>{id}</Option>
                ))}
              </Select>

              <Select
                defaultValue="all"
                style={{ width: 180 }}
                onChange={(value) => setSelectedGroup(value)}
              >
                <Option value="all">All Fields</Option>
                <Option value="contact">Contact Info Only</Option>
                <Option value="messaging">Messaging Only</Option>
              </Select>

              <span>Personalize #:</span>
              <Input
                type="number"
                min={1}
                value={personalizeCount}
                onChange={(e) => setPersonalizeCount(Number(e.target.value))}
                style={{ width: 80 }}
              />
              <Button
                type="primary"
                onClick={() =>{
                console.log("Triggering personalize with:", selectedCampaignId, personalizeCount);
                triggerAction("personalize-preview", {
                  campaign_id: selectedCampaignId,
                  count: personalizeCount,
                });
                }}
              >
                Personalize
              </Button>

              <span>Run Batch #:</span>
              <Input
                type="number"
                min={1}
                value={runBatchSize}
                onChange={(e) => setRunBatchSize(Number(e.target.value))}
                style={{ width: 80 }}
              />
              <Button
                type="primary"
                onClick={() =>
                  triggerAction("run-campaign", {
                    campaign_id: selectedCampaignId,
                    batch_size: runBatchSize,
                  })
                }
              >
                Run Campaign
              </Button>

              <Button onClick={() => triggerAction("follow-up")}>Send Follow-ups</Button>
              <Button onClick={() => downloadAsCSV(campaigns, "campaigns.csv")}>Export CSV</Button>
            </Space>
          </Space>
          {loading ? <Spin /> : (
            <Table
              rowKey="id"
              dataSource={campaigns}
              scroll={{ x: "max-content" }}
              columns={columns}
              components={components}
              size="small"
            />
          )}
           <Modal
            visible={!!viewRecord}
            onCancel={() => setViewRecord(null)}
            footer={null}
            title={`Record Details - ${viewRecord?.email}`}
            width={800}
          >
            <Descriptions column={1} bordered size="small" labelStyle={{ width: '200px' }}>
              {Object.entries(viewRecord || {}).map(([key, value]) => (
                <Descriptions.Item key={key} label={key.replace(/_/g, ' ')}>
                  {typeof value === "string" && value.length > 200 ? (
                    <div style={{ whiteSpace: "pre-wrap" }}>{value}</div>
                  ) : (
                    String(value)
                  )}
                </Descriptions.Item>
              ))}
            </Descriptions>
          </Modal>
        </TabPane>
        <TabPane tab="Rate Limits" key="limits">
          <Button onClick={fetchRateLimits} style={{ marginBottom: 16 }}>
            Refresh
          </Button>
          {loading ? (
            <Spin />
          ) : (
            <Table rowKey="user" dataSource={rateLimits} columns={rateLimitColumns} size="small" />
          )}
        </TabPane>
      </Tabs>
    </Card>
  );
}
